import { useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

/**
 * useFeedActions — bundles the "viewer interaction" handlers for the
 * landing feed: applaud, comment-toggle, follow toggle, save-to-trail
 * toggle, comments fetch/submit, plus the trailIds + comments local
 * state those handlers mutate. Lives at
 * /app/frontend/src/pages/feed/useFeedActions.js.
 *
 * Why bundle these together?
 *   They all share the same access-control gating pattern (signed-out
 *   → SignInPrompt, signed-in-but-broke → CreditsPrompt), and they all
 *   mutate state that lives inside the FeedPage but is otherwise just
 *   plumbed through into the persistent player overlay. Extracting them
 *   together keeps that ownership concentrated and shrinks FeedPage.jsx
 *   below the 600-line CI cap.
 *
 * What stays in the page:
 *   - playback navigation (handlePlayAll / handleVideoEnded / handleSkipNext
 *     etc.) lives in useFeedPlayback — it depends on `activeVideos`/
 *     `currentVideoIndex` and the PlayerContext refs, which is a different
 *     concern.
 *   - share is in useFeedShare — same reason.
 *
 * Inputs:
 *   user             — current user object (or null when signed out)
 *   token            — auth token (used by the trail-IDs hydration effect)
 *   updateCredits    — AuthContext setter; called optimistically after
 *                      an applaud/comment so the credit balance in the
 *                      header refreshes immediately.
 *   activeVideo      — the currently-playing video (activeVideos[index]).
 *                      Pass the resolved video, not the index, so the
 *                      hook doesn't need to know about the playlist.
 *   prompts          — { setShowSignInPrompt, setPendingAction,
 *                        setShowCreditsPrompt, setShowComments,
 *                        showComments }. Owned by the page so the
 *                        modals render alongside the player.
 *
 * Returns: { trailIds, setTrailIds, comments, setComments, newComment,
 *            setNewComment, loadingComments, submittingComment,
 *            applaudCount, followingUsers,
 *            handleApplaud, handleCommentClick, handleFollowFromFeed,
 *            handleToggleTrail, handleSubmitComment, fetchComments }
 */
export function useFeedActions({ user, token, updateCredits, activeVideo, prompts }) {
  const [trailIds, setTrailIds] = useState(() => new Set());
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [applaudCount, setApplaudCount] = useState({});
  const [followingUsers, setFollowingUsers] = useState({});

  const { setShowSignInPrompt, setPendingAction, setShowCreditsPrompt, setShowComments, showComments } = prompts;

  const fetchComments = useCallback(async (videoId) => {
    if (!videoId) return;
    setLoadingComments(true);
    try {
      const res = await api.get(`/videos/${videoId}/comments`);
      setComments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setComments([]);
    }
    setLoadingComments(false);
  }, []);

  const handleApplaud = useCallback(async () => {
    if (!user) {
      setShowCreditsPrompt('applaud');
      return;
    }
    if (!activeVideo) return;
    try {
      const res = await api.post(`/videos/${activeVideo.id}/applaud`, {});
      setApplaudCount(prev => ({
        ...prev,
        [activeVideo.id]: (prev[activeVideo.id] || 0) + 1,
      }));
      // BUG FIX (Jun 7): the old code called `updateCredits(-0.1)`
      // expecting it to be a delta — but updateCredits SETS the
      // absolute balance, so the UI ended up showing -0.1 cr after
      // every applaud regardless of the actual server-side math.
      // Server-side has always been correct (charges 0.50 once);
      // we just need to display what the server actually now holds.
      if (typeof res.data?.new_balance === 'number') {
        updateCredits(res.data.new_balance);
      }
      toast.success("Applauded! 👏");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to applaud");
    }
  }, [user, activeVideo, updateCredits, setShowCreditsPrompt]);

  const handleCommentClick = useCallback(() => {
    if (!user) {
      setShowCreditsPrompt('comment');
      return;
    }
    setShowComments(!showComments);
  }, [user, showComments, setShowComments, setShowCreditsPrompt]);

  const handleFollowFromFeed = useCallback(async (username) => {
    if (!user) {
      toast.error("Please sign in to follow users");
      return;
    }
    if (user.username === username) {
      toast.error("You can't follow yourself");
      return;
    }
    const isCurrentlyFollowing = followingUsers[username];
    try {
      if (isCurrentlyFollowing) {
        await api.delete(`/users/${username}/follow`);
        setFollowingUsers(prev => ({ ...prev, [username]: false }));
        toast.success(`Unfollowed @${username}`);
      } else {
        await api.post(`/users/${username}/follow`, {});
        setFollowingUsers(prev => ({ ...prev, [username]: true }));
        toast.success(`Following @${username}! Their videos will appear in My Feed.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update follow status");
    }
  }, [user, followingUsers]);

  const handleToggleTrail = useCallback(async () => {
    if (!user) {
      // Re-use the existing sign-in prompt flow — viewers who try to
      // save without an account see the same modal as comment/applaud.
      setShowSignInPrompt(true);
      setPendingAction('trail');
      return;
    }
    if (!activeVideo) return;
    const wasSaved = trailIds.has(activeVideo.id);
    // Optimistic flip so the icon feels instant.
    setTrailIds(prev => {
      const next = new Set(prev);
      if (wasSaved) next.delete(activeVideo.id); else next.add(activeVideo.id);
      return next;
    });
    try {
      const res = await api.post(`/videos/${activeVideo.id}/trail`, {});
      // Reconcile against server truth in case of race.
      setTrailIds(prev => {
        const next = new Set(prev);
        if (res.data?.saved) next.add(activeVideo.id); else next.delete(activeVideo.id);
        return next;
      });
      toast.success(res.data?.saved ? "Saved to your Trail" : "Removed from your Trail");
    } catch (err) {
      // Roll back optimistic update on failure.
      setTrailIds(prev => {
        const next = new Set(prev);
        if (wasSaved) next.add(activeVideo.id); else next.delete(activeVideo.id);
        return next;
      });
      toast.error(err.response?.data?.detail || "Couldn't update Trail");
    }
  }, [user, activeVideo, trailIds, setShowSignInPrompt, setPendingAction]);

  const handleSubmitComment = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!newComment.trim() || !activeVideo) return;
    if (!user) {
      setShowSignInPrompt(true);
      setPendingAction('comment');
      return;
    }
    if (user.credits < 0.20) {
      setShowCreditsPrompt('comment');
      return;
    }
    setSubmittingComment(true);
    try {
      const res = await api.post(`/videos/${activeVideo.id}/comments`, { content: newComment });
      setComments(prev => [res.data.comment || res.data, ...prev]);
      setNewComment("");
      if (res.data.new_balance !== undefined) {
        updateCredits(res.data.new_balance);
      }
      toast.success("Comment added!");
    } catch (err) {
      if (err.response?.status === 402) {
        setShowCreditsPrompt('comment');
      } else {
        toast.error(err.response?.data?.detail || "Failed to add comment");
      }
    }
    setSubmittingComment(false);
  }, [newComment, activeVideo, user, updateCredits, setShowSignInPrompt, setPendingAction, setShowCreditsPrompt]);

  // Reply to an existing top-level comment. Posts with parent_id and
  // appends the new row to the parent's `replies` array so the UI stays
  // in the threaded shape returned by GET /comments.
  const handleSubmitReply = useCallback(async (parentId, replyText) => {
    const text = (replyText || "").trim();
    if (!text || !activeVideo || !parentId) return { ok: false };
    if (!user) {
      setShowSignInPrompt(true);
      setPendingAction('comment');
      return { ok: false };
    }
    if (user.credits < 0.20) {
      setShowCreditsPrompt('comment');
      return { ok: false };
    }
    try {
      const res = await api.post(
        `/videos/${activeVideo.id}/comments`,
        { content: text, parent_id: parentId },
      );
      const reply = res.data.comment || res.data;
      setComments(prev => prev.map(c => (
        c.id === parentId
          ? { ...c, replies: [...(c.replies || []), reply] }
          : c
      )));
      if (res.data.new_balance !== undefined) {
        updateCredits(res.data.new_balance);
      }
      toast.success("Reply added!");
      return { ok: true };
    } catch (err) {
      if (err.response?.status === 402) {
        setShowCreditsPrompt('comment');
      } else {
        toast.error(err.response?.data?.detail || "Failed to reply");
      }
      return { ok: false };
    }
  }, [activeVideo, user, updateCredits, setShowSignInPrompt, setPendingAction, setShowCreditsPrompt]);

  return {
    trailIds, setTrailIds,
    comments, setComments,
    newComment, setNewComment,
    loadingComments,
    submittingComment,
    applaudCount,
    followingUsers,
    handleApplaud, handleCommentClick, handleFollowFromFeed,
    handleToggleTrail, handleSubmitComment, handleSubmitReply, fetchComments,
  };
}
